import { Check, XCircle, Robot, Eye } from "@phosphor-icons/react";
import type { SingleConditionResult, ConditionUserOverride } from "../types";
import { AIDetailsPanel } from "./AIDetailsPanel";

interface ConditionResultTreeProps {
  result: SingleConditionResult;
  depth?: number;
  fileId?: string;
  questionId?: number;
  isSaved?: boolean;
  expandedAIDetails: Set<string>;
  onToggleAIDetails: (key: string) => void;
  getEffectiveConditionPassed: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => boolean;
  getConditionOverride: (fileId: string, questionId: number, conditionId: string) => ConditionUserOverride | undefined;
  onToggleConditionOverride: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => void;
  onUpdateOverrideReason: (fileId: string, questionId: number, conditionId: string, reason: string) => void;
}

export function ConditionResultTree({
  result,
  depth = 0,
  fileId,
  questionId,
  isSaved,
  expandedAIDetails,
  onToggleAIDetails,
  getEffectiveConditionPassed,
  getConditionOverride,
  onToggleConditionOverride,
  onUpdateOverrideReason,
}: ConditionResultTreeProps) {
  const isGroup = result.conditionType === 'group';
  const isAICondition = result.conditionType === 'AI_QUESTION' || result.conditionType === 'AI_REFERENCE';
  const aiDetailsKey = `condition-${result.conditionId}`;
  const indent = depth * 12;

  const override = fileId && questionId !== undefined
    ? getConditionOverride(fileId, questionId, result.conditionId)
    : undefined;
  const effectivePassed = fileId && questionId !== undefined
    ? getEffectiveConditionPassed(fileId, questionId, result.conditionId, result.passed)
    : result.passed;

  return (
    <div key={result.conditionId} style={{ marginLeft: indent }}>
      <div className={`flex items-start gap-2 py-1 ${depth > 0 ? 'border-l-2 border-neutral-200 pl-2' : ''}`}>
        {effectivePassed ? (
          <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" weight="bold" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" weight="bold" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium text-neutral-700">
              {result.description}
            </span>
            {isAICondition && (
              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-violet-100 text-violet-700">
                <Robot className="h-2.5 w-2.5" />
                AI
              </span>
            )}
            {result.aiDetails?.interpretedAs && !override?.overridden && (
              <span className={`text-[9px] px-1 py-0.5 rounded ${
                result.aiDetails.interpretedAs === 'yes' ? 'bg-green-100 text-green-700' :
                result.aiDetails.interpretedAs === 'no' ? 'bg-red-100 text-red-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {result.aiDetails.interpretedAs === 'yes' ? 'Ja' :
                 result.aiDetails.interpretedAs === 'no' ? 'Nee' : 'Onduidelijk'}
              </span>
            )}
            {override?.overridden && (
              <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] bg-amber-100 text-amber-700 border border-amber-300">
                ⚠️ Overschreven
              </span>
            )}
            {!isGroup && !isSaved && fileId && questionId !== undefined && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleConditionOverride(fileId, questionId, result.conditionId, result.passed);
                }}
                className={`text-[9px] px-1 py-0.5 rounded border transition-colors ${
                  override?.overridden
                    ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100'
                    : 'bg-neutral-50 text-neutral-600 border-neutral-300 hover:bg-neutral-100'
                }`}
                title={override?.overridden ? 'Herstel originele waarde' : 'Overschrijf dit resultaat'}
              >
                {override?.overridden ? 'Herstel' : 'Overschrijf'}
              </button>
            )}
          </div>

          {override?.overridden && !isSaved && fileId && questionId !== undefined && (
            <div className="mt-1">
              <input
                type="text"
                value={override.reason || ''}
                onChange={(e) => onUpdateOverrideReason(fileId, questionId, result.conditionId, e.target.value)}
                placeholder="Reden voor overschrijving (optioneel)"
                className="w-full text-[9px] border border-amber-200 rounded px-1.5 py-1 bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-400"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {!isGroup && result.details && (
            <div className="mt-0.5 text-[10px] text-neutral-500 space-y-0.5">
              {result.details.checkedField && (
                <div>Veld: <span className="font-medium">{result.details.checkedField}</span></div>
              )}
              {result.details.expectedValue && (
                <div>Verwacht: <span className="font-medium text-neutral-600">{result.details.expectedValue}</span></div>
              )}
              {result.details.actualValue && (
                <div>
                  Werkelijk: <span className={`font-medium ${result.passed ? 'text-green-600' : 'text-red-500'}`}>
                    {result.details.actualValue}
                  </span>
                </div>
              )}
              {result.details.daysUntilExpiry !== undefined && (
                <div className={result.details.daysUntilExpiry < 0 ? 'text-red-500' : result.details.daysUntilExpiry <= 30 ? 'text-amber-600' : 'text-green-600'}>
                  {result.details.daysUntilExpiry < 0
                    ? `${Math.abs(result.details.daysUntilExpiry)} dagen verlopen`
                    : `${result.details.daysUntilExpiry} dagen tot verloop`}
                </div>
              )}
            </div>
          )}

          {isAICondition && result.aiDetails && (
            <div className="mt-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleAIDetails(aiDetailsKey);
                }}
                className="text-[9px] text-violet-600 hover:text-violet-700 flex items-center gap-0.5"
              >
                <Eye className="h-3 w-3" />
                {expandedAIDetails.has(aiDetailsKey) ? 'Verberg AI details' : 'Bekijk AI details'}
              </button>

              {expandedAIDetails.has(aiDetailsKey) && (
                <AIDetailsPanel aiDetails={result.aiDetails} variant="condition" />
              )}
            </div>
          )}
        </div>
      </div>

      {isGroup && result.children && result.children.length > 0 && (
        <div className="ml-2">
          {result.children.map((child) => (
            <ConditionResultTree
              key={child.conditionId}
              result={child}
              depth={depth + 1}
              fileId={fileId}
              questionId={questionId}
              isSaved={isSaved}
              expandedAIDetails={expandedAIDetails}
              onToggleAIDetails={onToggleAIDetails}
              getEffectiveConditionPassed={getEffectiveConditionPassed}
              getConditionOverride={getConditionOverride}
              onToggleConditionOverride={onToggleConditionOverride}
              onUpdateOverrideReason={onUpdateOverrideReason}
            />
          ))}
        </div>
      )}
    </div>
  );
}
